import {
  createChildRequestSchema,
  createInvitationRequestSchema,
  guardianCapabilityNames,
  type ApproveChildRequest,
  type ChildRequestStatus,
  type CreateChildRequest,
  type CreateInvitationRequest,
  type GuardianCapabilityName,
  type GuardianRelationship,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

/** What the guardian typed about the child. */
export interface ChildRequestForm {
  givenName: string;
  familyName: string;
  dateOfBirth: string;
  relationship: GuardianRelationship;
  note: string;
}

export const emptyChildRequestForm: ChildRequestForm = {
  givenName: '',
  familyName: '',
  dateOfBirth: '',
  relationship: 'parent',
  note: '',
};

/** The request to send, or null while the form is incomplete or invalid. */
export function childRequestBody(form: ChildRequestForm): CreateChildRequest | null {
  const parsed = createChildRequestSchema.safeParse(form);
  return parsed.success ? parsed.data : null;
}

export const childRequestStatusLabelKeys: Readonly<Record<ChildRequestStatus, TranslationKey>> = {
  pending: 'childRequests.status.pending',
  approved: 'childRequests.status.approved',
  rejected: 'childRequests.status.rejected',
  withdrawn: 'childRequests.status.withdrawn',
};

/** The reviewer's choice: one of the offered athletes, or a new athlete record. */
export type ChildRequestMatch = { kind: 'existing'; athleteId: string } | { kind: 'new' };

/**
 * The approval body. Choosing a new athlete while candidates were shown is the reviewer's explicit
 * confirmation that the child is a different person, so the duplicate warning is acknowledged.
 */
export function childRequestApprovalBody(
  match: ChildRequestMatch,
  capabilities: Record<GuardianCapabilityName, boolean>,
  candidatesShown: boolean,
): ApproveChildRequest {
  return match.kind === 'existing'
    ? { athleteId: match.athleteId, confirmDuplicate: false, capabilities }
    : { athleteId: null, confirmDuplicate: candidatesShown, capabilities };
}

/** An approval offers every capability; the reviewer removes what the family should not have. */
export function allCapabilities(): Record<GuardianCapabilityName, boolean> {
  return Object.fromEntries(guardianCapabilityNames.map((name) => [name, true])) as Record<
    GuardianCapabilityName,
    boolean
  >;
}

/** What staff type to invite a parent who has no record in the club yet. */
export interface ParentInvitationForm {
  email: string;
  givenName: string;
  familyName: string;
}

export const emptyParentInvitationForm: ParentInvitationForm = {
  email: '',
  givenName: '',
  familyName: '',
};

/** A guardian invitation that creates the parent's record; null while the form is not valid. */
export function parentInvitationBody(form: ParentInvitationForm): CreateInvitationRequest | null {
  const parsed = createInvitationRequestSchema.safeParse({
    recipientEmail: form.email.trim(),
    permissionBundles: ['guardian'],
    newGuardian: { givenName: form.givenName, familyName: form.familyName },
  });
  return parsed.success ? parsed.data : null;
}
